package com.videoanalyzer.web.api

import com.videoanalyzer.web.config.Config
import com.videoanalyzer.web.util.Logger
import com.videoanalyzer.web.models.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.localStorage
import kotlinx.serialization.json.Json

/**
 * API client for communicating with our backend.
 *
 * This is like Retrofit in Android - handles HTTP calls and JSON parsing.
 *
 * FEATURES:
 * - Automatic JSON serialization/deserialization
 * - Auth token management
 * - Error handling
 * - Session expiry detection
 */
object ApiClient {

    // Backend URL from config
    private val BASE_URL = Config.API_BASE_URL

    // Storage key for auth token
    private const val TOKEN_KEY = "auth_token"

    // Callback for when session expires (401 response)
    private var onSessionExpired: (() -> Unit)? = null

    /**
     * Set callback to be invoked when session expires.
     * The App should register this to handle automatic logout.
     */
    fun setSessionExpiredCallback(callback: () -> Unit) {
        onSessionExpired = callback
    }

    /**
     * Handle 401 Unauthorized response - session has expired.
     */
    private fun handleSessionExpired() {
        Logger.log("Auth", "Session expired - logging out")
        clearToken()
        onSessionExpired?.invoke()
    }

    // HTTP client configured with JSON support
    private val client = HttpClient(Js) {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                encodeDefaults = true
            })
        }
    }

    // ============================================================
    // Token Management
    // ============================================================

    /**
     * Get stored auth token.
     */
    fun getToken(): String? {
        return try {
            localStorage.getItem(TOKEN_KEY)
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Store auth token.
     */
    fun setToken(token: String) {
        try {
            localStorage.setItem(TOKEN_KEY, token)
        } catch (e: Exception) {
            Logger.error("Failed to store token", e)
        }
    }

    /**
     * Clear auth token (logout).
     */
    fun clearToken() {
        try {
            localStorage.removeItem(TOKEN_KEY)
        } catch (e: Exception) {
            Logger.error("Failed to clear token", e)
        }
    }

    /**
     * Check if user has a stored token.
     */
    fun hasToken(): Boolean = getToken() != null

    // ============================================================
    // Auth API Calls
    // ============================================================

    /**
     * Get current user profile.
     *
     * @return AuthResponse if authenticated, null if not
     */
    suspend fun getCurrentUser(): AuthResponse? {
        val token = getToken() ?: return null

        return try {
            val response = client.get("$BASE_URL/api/auth/me") {
                header("Authorization", "Bearer $token")
            }

            if (response.status == HttpStatusCode.OK) {
                response.body<AuthResponse>()
            } else {
                // Token might be expired
                clearToken()
                null
            }
        } catch (e: Exception) {
            Logger.error("Error getting user", e)
            null
        }
    }

    /**
     * Create/update profile after Supabase auth.
     */
    suspend fun authCallback(userId: String, email: String): AuthResponse? {
        return try {
            val response = client.post("$BASE_URL/api/auth/callback") {
                contentType(ContentType.Application.Json)
                setBody(mapOf("userId" to userId, "email" to email))
            }

            if (response.status == HttpStatusCode.OK) {
                response.body<AuthResponse>()
            } else {
                null
            }
        } catch (e: Exception) {
            Logger.error("Error in auth callback", e)
            null
        }
    }

    // ============================================================
    // Video API Calls
    // ============================================================

    /**
     * Parse a YouTube URL and get video ID.
     */
    suspend fun parseVideoUrl(url: String): Result<String> {
        return try {
            val response = client.post("$BASE_URL/api/video/parse") {
                contentType(ContentType.Application.Json)
                setBody(mapOf("url" to url))
            }

            if (response.status == HttpStatusCode.OK) {
                val body = response.body<Map<String, String>>()
                Result.success(body["videoId"] ?: "")
            } else {
                val error = response.body<ErrorResponse>()
                Result.failure(Exception(error.error))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get video metadata from YouTube.
     */
    suspend fun getVideoMetadata(videoId: String): Result<VideoMetadata> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/video/$videoId") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<VideoMetadata>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> {
                    val error = response.body<ErrorResponse>()
                    Result.failure(Exception(error.error))
                }
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // ============================================================
    // Analysis API Calls
    // ============================================================

    /**
     * Run full video analysis.
     *
     * @return AnalysisResponse on success, or specific error
     */
    suspend fun analyzeVideo(videoUrl: String): AnalysisApiResult {
        val token = getToken() ?: return AnalysisApiResult.NotAuthenticated

        return try {
            val response = client.post("$BASE_URL/api/analyze") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(mapOf("videoUrl" to videoUrl))
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    val body = response.body<AnalysisResponse>()
                    AnalysisApiResult.Success(body)
                }
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    AnalysisApiResult.NotAuthenticated
                }
                HttpStatusCode.PaymentRequired -> {
                    val body = response.body<UsageLimitResponse>()
                    AnalysisApiResult.LimitReached(body.currentUsage, body.limit)
                }
                else -> {
                    val error = try {
                        response.body<ErrorResponse>().error
                    } catch (e: Exception) {
                        "Analysis failed"
                    }
                    AnalysisApiResult.Error(error)
                }
            }
        } catch (e: Exception) {
            AnalysisApiResult.Error(e.message ?: "Network error")
        }
    }

    /**
     * Get analysis history.
     */
    suspend fun getHistory(limit: Int = 10, offset: Int = 0): Result<AnalysisHistoryResponse> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/analyses") {
                header("Authorization", "Bearer $token")
                parameter("limit", limit)
                parameter("offset", offset)
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<AnalysisHistoryResponse>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to load history"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get specific analysis by ID.
     */
    suspend fun getAnalysis(id: String): Result<FullAnalysis> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/analyses/$id") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<FullAnalysis>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Analysis not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // ============================================================
    // Payment API Calls
    // ============================================================

    /**
     * Create Stripe checkout session.
     */
    suspend fun createCheckout(successUrl: String, cancelUrl: String): Result<CheckoutResponse> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.post("$BASE_URL/api/payments/create-checkout") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(mapOf("successUrl" to successUrl, "cancelUrl" to cancelUrl))
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<CheckoutResponse>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> {
                    val error = response.body<ErrorResponse>()
                    Result.failure(Exception(error.error))
                }
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Create Stripe customer portal session.
     */
    suspend fun createPortal(returnUrl: String): Result<String> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.post("$BASE_URL/api/payments/create-portal") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(mapOf("returnUrl" to returnUrl))
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    val body = response.body<Map<String, String>>()
                    Result.success(body["portalUrl"] ?: "")
                }
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to create portal"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get subscription status.
     */
    suspend fun getSubscriptionStatus(): Result<SubscriptionStatus> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/payments/status") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<SubscriptionStatus>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to get status"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Sync subscription status from Stripe.
     * Returns updated user profile.
     */
    suspend fun syncSubscription(): Result<UserProfile> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.post("$BASE_URL/api/payments/sync") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    val body = response.body<kotlinx.serialization.json.JsonObject>()
                    val profileJson = body["profile"]
                    if (profileJson != null) {
                        val profile = kotlinx.serialization.json.Json.decodeFromJsonElement(
                            UserProfile.serializer(),
                            profileJson
                        )
                        Result.success(profile)
                    } else {
                        Result.failure(Exception("No profile in response"))
                    }
                }
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to sync subscription"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Get available credit packs.
     */
    suspend fun getCreditPacks(): Result<CreditPacksResponse> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/payments/credit-packs") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<CreditPacksResponse>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to get credit packs"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Create checkout session for buying credits.
     */
    suspend fun buyCredits(packId: String, successUrl: String, cancelUrl: String): Result<CheckoutResponse> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.post("$BASE_URL/api/payments/buy-credits") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(mapOf(
                    "packId" to packId,
                    "successUrl" to successUrl,
                    "cancelUrl" to cancelUrl
                ))
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<CheckoutResponse>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> {
                    val error = response.body<ErrorResponse>()
                    Result.failure(Exception(error.error))
                }
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // ============================================================
    // Trends API Calls
    // ============================================================

    /**
     * Get trends summary (average score, trend direction, re-analyze reminders).
     */
    suspend fun getTrendsSummary(): Result<TrendsSummary> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/trends/summary") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<TrendsSummary>())
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to load trends"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // ============================================================
    // Error Reporting
    // ============================================================

    /**
     * Report a frontend error to the backend (which forwards to Sentry).
     * Call this in catch blocks to track errors in production.
     */
    suspend fun reportError(
        message: String,
        stack: String? = null,
        context: String? = null
    ) {
        try {
            client.post("$BASE_URL/api/report-error") {
                contentType(ContentType.Application.Json)
                setBody(mapOf(
                    "message" to message,
                    "stack" to stack,
                    "context" to context,
                    "url" to kotlinx.browser.window.location.href,
                    "userAgent" to kotlinx.browser.window.navigator.userAgent
                ))
            }
        } catch (e: Exception) {
            // Silently fail - don't crash while reporting errors
            Logger.error("Failed to report error", e)
        }
    }

    /**
     * Delete user account and all data (GDPR right to erasure).
     */
    suspend fun deleteAccount(): Result<Boolean> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.delete("$BASE_URL/api/auth/account") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> Result.success(true)
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> {
                    val body = response.body<kotlinx.serialization.json.JsonObject>()
                    val error = body["error"]?.toString() ?: "Failed to delete account"
                    Result.failure(Exception(error))
                }
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Export user data (GDPR data portability).
     * Returns the export data as a JSON string.
     */
    suspend fun exportData(): Result<String> {
        val token = getToken() ?: return Result.failure(Exception("Not authenticated"))

        return try {
            val response = client.get("$BASE_URL/api/auth/export") {
                header("Authorization", "Bearer $token")
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    val body = response.bodyAsText()
                    Result.success(body)
                }
                HttpStatusCode.Unauthorized -> {
                    handleSessionExpired()
                    Result.failure(Exception("Session expired"))
                }
                else -> Result.failure(Exception("Failed to export data"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

/**
 * Result type for analysis API calls.
 */
sealed class AnalysisApiResult {
    data class Success(val response: AnalysisResponse) : AnalysisApiResult()
    data class LimitReached(val currentUsage: Int, val limit: Int) : AnalysisApiResult()
    data class Error(val message: String) : AnalysisApiResult()
    object NotAuthenticated : AnalysisApiResult()
}
